'use client';

import React, { useMemo, useCallback } from 'react';
import Link from 'next/link';
import { trackEvent } from '@/components/analytics';
import { sortTariffs, formatDays } from './utils/filterTariffs';
import AffiliateLink from '@/components/common/AffiliateLink';
import { ConvMoney, CurrencySwitcher } from '@/components/common/CurrencyContext';
import { t as tr } from '@/i18n/translations';

/**
 * Quiz results — "Gallery · Field" design (qz-results / qz-row).
 * Sorting/filter logic unchanged; only markup/styling is GF.
 */
export default function QuizResults({
    filteredTariffs = [],
    allTariffs = [],
    showAllTariffs = false,
    onShowAllToggle,
    onBackToQuestions,
    onStartOver,
    sortConfig,
    onSort,
    translations: t,
    lang,
    countrySlug,
}) {
    const ui = tr(lang, 'esimToolsUi').results;

    const tariffsToDisplay = useMemo(() => {
        const tariffs = showAllTariffs ? allTariffs : filteredTariffs;
        return sortTariffs(tariffs, sortConfig);
    }, [showAllTariffs, allTariffs, filteredTariffs, sortConfig]);

    const handleTariffClick = useCallback((tariff) => {
        trackEvent('click', 'esim_quiz', 'plan_selected', {
            provider: tariff.provider,
            plan_type: tariff.unlimited ? 'unlimited' : `${tariff.data}GB`,
            duration: tariff.duration,
        });
    }, []);

    const covCell = (tariff) => {
        const cov = tariff.coverage || 'local';
        if (cov === 'local') return <span className="cov-loc">{ui.coverageLocal}</span>;
        const label = cov === 'global' ? ui.coverageGlobal : ui.coverageRegional;
        const countries = Array.isArray(tariff.regions) && tariff.regions.length > 1 ? tariff.regions.join(', ') : null;
        if (!countries) return <span className="cov-loc">{label}</span>;
        return (
            <span className="cov-reg" tabIndex={0}>
                {label}
                <span className="cov-tip" role="tooltip">{countries}</span>
            </span>
        );
    };

    const sortChip = (key, label) => (
        <button type="button" className={`chip ${sortConfig.key === key ? 'on' : ''}`} onClick={() => onSort(key)} aria-pressed={sortConfig.key === key}>
            {label}{sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}
        </button>
    );

    const summary = tariffsToDisplay.length > 0
        ? (showAllTariffs
            ? ui.showing(tariffsToDisplay.length)
            : ui.found(tariffsToDisplay.length))
        : t.noResultsDesc;

    return (
        <div className="qz-results" style={{ display: 'block' }}>
            <p className="qz-rkick">{ui.kick}</p>
            <h2 className="qz-rtitle">{t.results}</h2>
            <p className="qz-rsum">{summary}</p>

            <div className="qz-controls">
                <div className="qz-sort">
                    <span>{t.sortBy}:</span>
                    {sortChip('price', t.sortByPrice)}
                    {sortChip('data', t.sortByData)}
                    {sortChip('duration', t.sortByValidity)}
                    {showAllTariffs && sortChip('relevance', t.sortByRelevance)}
                </div>
                <div className="qz-controls-right">
                    <CurrencySwitcher />
                    <button type="button" className="qz-toggle" onClick={onShowAllToggle}>
                        {showAllTariffs ? t.showFiltered : t.showAll} →
                    </button>
                </div>
            </div>

            {tariffsToDisplay.length > 0 ? (
                <div className="gf-plans gf-plans-esim qz-ledger">
                    <div className="gf-plan-row head">
                        <span>{t.data}</span>
                        <span>{t.validity}</span>
                        <span>{ui.coverageCol}</span>
                        <span>{t.price}</span>
                        <span>{ui.perGbCol}</span>
                        {/* invisible clone of the row action so the header's last
                            (auto) track reserves the same width — keeps columns aligned */}
                        <span className="sel" aria-hidden="true" style={{ visibility: 'hidden' }}>{ui.select}</span>
                    </div>
                    {tariffsToDisplay.map((tariff, index) => {
                        const code = tariff.providerId;
                        const provHref = `/${lang}/${countrySlug}/esim/${code}`;
                        const logo = tariff.providerLogo || `/images/providers/${code}.svg`;
                        const unlimited = tariff.unlimited || tariff.isUnlimited;
                        const cur = tariff.priceCurrency;
                        const priceAmt = Number(tariff.price || tariff.priceAmount || 0);
                        const dataTxt = tariff.isPayAsYouGo
                            ? ui.payAsYouGo
                            : unlimited
                                ? ui.unlimited
                                : `${tariff.data} ${tariff.dataUnit === 'GB' ? ui.gb : tariff.dataUnit}`;
                        return (
                            <div className="gf-plan-row" key={tariff.id || index}>
                                <span className="d">
                                    <Link className="op-logo-link" href={provHref} aria-label={tariff.provider}>
                                        <img className="op-logo" src={logo} alt={tariff.provider} loading="lazy"
                                            onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = '1'; e.currentTarget.src = '/images/providers/default.svg' } }} />
                                    </Link>
                                    <span className="d-tx">
                                        <Link className="op-tag" href={provHref}>{tariff.provider}</Link>
                                        <span className="gbline">
                                            {dataTxt}
                                            {tariff.calls ? <span className="badge-calls">{ui.calls}</span> : null}
                                        </span>
                                    </span>
                                </span>
                                <span className="v">{formatDays(tariff.duration, lang)}</span>
                                <span className="cov">{covCell(tariff)}</span>
                                <ConvMoney className="p" amount={priceAmt} currency={cur} />
                                <span className="per">{unlimited || tariff.isPayAsYouGo ? '—' : <ConvMoney amount={Number(tariff.pricePerGb || 0)} currency={cur} suffix={ui.perGbSuffix} />}</span>
                                {tariff.link ? (
                                    <AffiliateLink href={tariff.link} provider={code} vertical="esim" country={countrySlug} position="quiz" price={tariff.price} onClick={() => handleTariffClick(tariff)} className="sel">
                                        {ui.select}
                                    </AffiliateLink>
                                ) : (
                                    <Link href={provHref} className="sel">{ui.details}</Link>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="qz-empty">{t.noResultsDesc || t.noResults}</div>
            )}

            <div className="qz-foot">
                <button type="button" className="ghost" onClick={onBackToQuestions}>← {ui.backToQuestions}</button>
                <button type="button" className="ghost" onClick={onStartOver}>↻ {t.startOver}</button>
            </div>
        </div>
    );
}
